import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import React, { ReactNode } from 'react'
import styled from 'styled-components'

import { CAPACITY_BOX_COLORS } from '~/common/globalStyles'
import { withStyles } from '~/common/theme'

import Button, { ButtonProps } from '../Button'
import Loader from '../Loader'

interface AlertDialogProps {
  AgreeButtonProps?: ButtonProps
  agreeText: string
  content: ReactNode
  DisagreeButtonProps?: ButtonProps
  disagreeText: string
  loading?: boolean
  loadingContent?: boolean
  onAgree?: (event: any) => void
  onClose: DialogProps['onClose']
  onDisagree: () => void
  open: DialogProps['open']
  paperProps?: DialogProps['PaperProps']
  title: string
  withStyle?: any
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
}

const StyledButtonAccept = styled(Button)`
  margin-right: 50px;
  margin-bottom: 24px;
`
const StyledButtonReject = styled(Button)`
  margin-left: 50px;
  margin-bottom: 24px;
  &:hover {
    background-color: ${CAPACITY_BOX_COLORS.lightBlue};
  }
`
const AlertDialog: React.FC<AlertDialogProps> = ({
  AgreeButtonProps,
  agreeText,
  content,
  DisagreeButtonProps,
  disagreeText,
  disabled = false,
  loading = false,
  loadingContent = false,
  onAgree,
  onClose,
  onDisagree,
  open,
  paperProps,
  title,
  withStyle,
  onClick,
}: AlertDialogProps) => {
  const DialogComponent = withStyle ? withStyles(Dialog, withStyle) : Dialog

  return (
    <DialogComponent
      fullWidth
      maxWidth="xs"
      PaperProps={paperProps || {}}
      open={open}
      onClose={(event, reason) => {
        if (reason && reason !== 'backdropClick') {
          onClose && onClose(event, reason)
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid="alert-dialog"
      disableEnforceFocus
      onClick={onClick}
    >
      <DialogTitle
        style={{ margin: 'auto', display: 'flex' }}
        id="alert-dialog-title"
      >
        {title}
      </DialogTitle>

      {loadingContent ? <Loader /> : content}

      {loading && <Loader />}

      <DialogActions>
        {disagreeText && (
          <StyledButtonReject
            large
            color={'positive'}
            onClick={onDisagree}
            {...DisagreeButtonProps}
            disabled={loading}
          >
            {disagreeText}
          </StyledButtonReject>
        )}
        {agreeText && (
          <StyledButtonAccept
            large
            raised
            showBackgroundColorWithoutHover
            onClick={(event: Event) => {
              if (!loading && !disabled) {
                onAgree?.(event)
              }
            }}
            {...AgreeButtonProps}
            disabled={loadingContent || loading || disabled}
            color="newPrimary"
            data-testid="button-accept"
          >
            {agreeText}
          </StyledButtonAccept>
        )}
      </DialogActions>
    </DialogComponent>
  )
}

AlertDialog.defaultProps = {
  AgreeButtonProps: { color: 'negative' },
  DisagreeButtonProps: { color: 'primary' },
  agreeText: 'Agree',
  disagreeText: 'Disagree',
  open: false,
}

export default AlertDialog
